import { useNavigate } from 'react-router-dom';
import {
  IconClipboardList,
  IconHeartbeat,
  IconUsers,
  IconActivity,
  IconTarget,
  IconFileUpload,
  IconSignature,
  IconFile,
  IconCircle,
  IconCircleHalf2,
  IconCheck,
} from '@tabler/icons-react';
import type { OnboardingSection, SectionStatus } from '../types/onboarding';

interface TaskCardProps {
  section: OnboardingSection;
  status: SectionStatus;
}

const iconMap: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  IconClipboardList,
  IconHeartbeat,
  IconUsers,
  IconActivity,
  IconTarget,
  IconFileUpload,
  IconSignature,
};

// Status icon and color configuration
const statusConfig: Record<SectionStatus, {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  iconColor: string;
  bgColor: string;
  borderColor: string;
}> = {
  unseen: {
    icon: IconCircle,
    iconColor: 'text-gray-500',
    bgColor: 'bg-gray-800/30',
    borderColor: 'border-gray-600/30',
  },
  'in-progress': {
    icon: IconCircleHalf2,
    iconColor: 'text-amber-400',
    bgColor: 'bg-amber-900/30',
    borderColor: 'border-amber-500/30',
  },
  complete: {
    icon: IconCheck,
    iconColor: 'text-green-400',
    bgColor: 'bg-green-900/30',
    borderColor: 'border-green-500/30',
  },
};

export function TaskCard({ section, status }: TaskCardProps) {
  const navigate = useNavigate();

  const handleClick = () => {
    if (!section.isComingSoon) {
      navigate(section.path);
    }
  };

  const IconComponent = iconMap[section.icon] || IconFile;
  const config = statusConfig[status];
  const StatusIcon = config.icon;

  if (section.isComingSoon) {
    return (
      <div className="card opacity-50 cursor-not-allowed">
        <div className="flex items-center gap-4 p-4">
          <div className="w-12 h-12 rounded-lg bg-gray-800/50 flex items-center justify-center">
            <IconComponent size={24} className="text-gray-500" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-gray-400">{section.title}</h3>
              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-800 text-gray-500">
                Coming Soon
              </span>
            </div>
            <p className="text-sm text-gray-600 mt-1">{section.description}</p>
          </div>
        </div>
      </div>
    );
  }

  // Determine icon background based on status
  const iconBgClass = status === 'complete'
    ? 'bg-green-900/30 border border-green-500/30'
    : status === 'in-progress'
      ? 'bg-amber-900/30 border border-amber-500/30'
      : 'bg-cyan-900/30 border border-cyan-500/30';

  const iconTextClass = status === 'complete'
    ? 'text-green-400'
    : status === 'in-progress'
      ? 'text-amber-400'
      : 'text-cyan-400';

  return (
    <div
      onClick={handleClick}
      className={`card cursor-pointer transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_0_30px_rgba(0,214,245,0.15)] ${
        status === 'complete' ? 'opacity-75' : ''
      }`}
    >
      <div className="flex items-center gap-4 p-4">
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${iconBgClass}`}>
          <IconComponent size={24} className={iconTextClass} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-100">{section.title}</h3>
          <p className="text-sm text-gray-400 mt-1">{section.description}</p>
        </div>
        <div className="flex-shrink-0">
          <div className={`w-8 h-8 rounded-full ${config.bgColor} border ${config.borderColor} flex items-center justify-center`}>
            <StatusIcon size={20} className={config.iconColor} />
          </div>
        </div>
      </div>
    </div>
  );
}
